import {
  WSActions,
  WSCard,
  WSForm,
  WSInputTextarea,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import { ISharedFileRequestSettingsResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import React from "react";
import * as Yup from "yup";

import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../query/WSQuery";
import { useMutationUpdateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useMutationUpdateRequirementDefinition";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { StepComponentProps } from "../../../../shared/components/ModalFlow/types";
import { validatorStringGeneric } from "../../../../shared/validators/validatorStringGeneric";
import { getStartOfDay } from "../../../../utils/dates";
import { RequirementInstructionsView } from "../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { useCreateRequirementDefinitionSettings } from "../../hooks/useCreateRequirementDefinitionSettings";
import { useRequirementDefinitionSettings } from "../../hooks/useRequirementDefinitionSettings";
import { InsuranceInstructionsView } from "../../routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { RequirementDefinitionTemplate } from "../../routes/RouteCreateRequirementByTemplate/types";
import { getRequirementDefinitionTemplate } from "../../utils/getRequirementDefinitionTemplate";
import {
  convertFormApprovalDataToReviewStrategy,
  convertFormDataToExpirationStrategy
} from "../CreateRequirementDefinitionForm/utils";
import { RequirementInformationCard } from "../RequirementDefinitionInfoCard";

import {
  FlowCreateNewVersionSettingsData,
  FlowCreateNewVersionState
} from "./types";
import { getVersionNoteValidator } from "../../utils/validators";

const EXPIRATION_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  "expirationStrategy",
  "expirationDays",
  "manualRenewalAllowed"
];

const APPROVAL_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  "reviewStrategy",
  "isApprovalRequired"
];

const NON_SETTINGS_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  ...EXPIRATION_FIELDS,
  ...APPROVAL_FIELDS
];

const isFieldsChanged = (
  data: Partial<FlowCreateNewVersionSettingsData>,
  fields: typeof NON_SETTINGS_FIELDS
) => {
  return fields.some(field => field in data);
};

const isSomethingElseChanged = (
  data: Partial<FlowCreateNewVersionSettingsData>,
  fields: typeof NON_SETTINGS_FIELDS
) => {
  return Object.keys(data).some(dataKey => !fields.includes(dataKey as any));
};

export const StepReviewAndCreate: React.FC<
  StepComponentProps<FlowCreateNewVersionState>
> = ({ onBack, onSuccess, goToSideStep, state, setState }) => {
  const queryRequirementDefinition = useQueryRequirementDefinition(
    state.requirementDefinitionId
  );

  const { errors, submit, isLoading } =
    useCreateRequirementDefinitionSettings();

  const [updateRequirementDefinition, updateRequirementDefinitionMeta] =
    useMutationUpdateRequirementDefinition(state.requirementDefinitionId);

  const handleSubmit = async (
    state: FlowCreateNewVersionState,
    template: RequirementDefinitionTemplate,
    requirementDefinition: IRequirementDefinitionResponse
  ) => {
    let updateRequest: Parameters<typeof updateRequirementDefinition>[0] = {
      versionScope: state.transitionData?.scope!,
      versionTransitionExpiresAt: getStartOfDay(
        state.transitionData?.renewalDeadlineDays
      ),
      versionTransitionStrategy: state.transitionData?.transitionMethod!,
      versionNote: state.versionNote || ""
    };

    if (state.settingsData?.name !== requirementDefinition.name) {
      updateRequest.name = state.settingsData?.name;
    }

    if (state.settingsData?.description !== requirementDefinition.description) {
      updateRequest.description = state.settingsData?.description;
    }

    if (
      state?.changedData &&
      isSomethingElseChanged(state?.changedData!, NON_SETTINGS_FIELDS)
    ) {
      const settingsResponse = await submit({
        formData: {
          type: requirementDefinition.requirementType,
          ...state.settingsData!
        },
        template
      });

      updateRequest.dataSourceId = settingsResponse.dataSourceId;
    }

    if (isFieldsChanged(state?.changedData!, APPROVAL_FIELDS)) {
      updateRequest.reviewStrategy = convertFormApprovalDataToReviewStrategy({
        contextType: (
          querySettingsItem.data as ISharedFileRequestSettingsResponse
        )?.contextType,
        isApprovalRequired: !!state?.settingsData?.isApprovalRequired,
        reviewStrategy: state.settingsData?.reviewStrategy!,
        type: queryRequirementDefinition.data?.requirementType!
      });
    }

    if (isFieldsChanged(state?.changedData!, EXPIRATION_FIELDS)) {
      updateRequest = {
        ...updateRequest,
        ...convertFormDataToExpirationStrategy(state.settingsData!)
      };
    }
    await updateRequirementDefinition(updateRequest, {
      onSuccess() {
        goToSideStep("success");
      }
    });
  };

  const querySettingsItem = useRequirementDefinitionSettings({
    dataSourceId: queryRequirementDefinition.data?.dataSourceId,
    requirementType: queryRequirementDefinition.data?.requirementType
  });

  return (
    <WSQueries queries={{ queryRequirementDefinition, querySettingsItem }}>
      {({ queryRequirementDefinitionData, querySettingsItemData }) => {
        const template = getRequirementDefinitionTemplate(
          queryRequirementDefinitionData,
          querySettingsItemData
        );

        const acceptWingspanMasterPolicy =
          state.settingsData &&
          "acceptWingspanMasterPolicy" in state.settingsData
            ? state.settingsData.acceptWingspanMasterPolicy
            : undefined;

        return (
          <WSList gap="2XL">
            <WSText.Heading4>Review & create</WSText.Heading4>
            <WSText.ParagraphSm color="gray500">
              This step will allow you to review all settings before creating
              the new version. Coming soon.
            </WSText.ParagraphSm>

            <RequirementInformationCard
              description={state.settingsData?.description!}
              name={state.settingsData?.name!}
              template={template}
              version={queryRequirementDefinitionData.version + 1}
            />

            {template.instructions ||
            state.settingsData?.instructions ||
            template.isPolicy ? (
              <WSCard
                header={{
                  label: {
                    helperText:
                      "The following instructions will be shown to the contractors.",
                    text: "Instructions to contractors"
                  }
                }}
              >
                <WSList gap="2XL">
                  {template.isPolicy ? (
                    <InsuranceInstructionsView
                      allowMasterPolicy={acceptWingspanMasterPolicy}
                      insuranceName={template.name}
                      policyTypes={template.policyType}
                    />
                  ) : null}
                  {!template.isPolicy &&
                  (template.instructions ||
                    state.settingsData?.instructions) ? (
                    <WSCard>
                      <RequirementInstructionsView
                        instructions={
                          template.instructions ||
                          state.settingsData?.instructions!
                        }
                      />
                    </WSCard>
                  ) : null}
                </WSList>
              </WSCard>
            ) : null}

            <WSForm
              defaultValues={{
                versionNote: ""
              }}
              validationSchema={Yup.object({
                versionNote: getVersionNoteValidator(
                  queryRequirementDefinitionData.version + 1
                )
              })}
              onSubmit={async formData => {
                const newState: FlowCreateNewVersionState = {
                  ...state,
                  ...formData
                };

                setState(formData);
                await handleSubmit(
                  newState,
                  template,
                  queryRequirementDefinitionData
                );
              }}
            >
              <WSList gap="2XL">
                <WSCard
                  header={{
                    label: {
                      helperText:
                        "Add context about why this version was created or what changed. This note is only visible to your team.",
                      text: "Internal note"
                    }
                  }}
                >
                  <WSForm.Field
                    component={WSInputTextarea}
                    componentProps={{
                      pill: {
                        icon: "lock",
                        text: "Private",
                        theme: "neutral"
                      }
                    }}
                    label="Internal note"
                    name="versionNote"
                    placeholder="Add a note..."
                  />
                </WSCard>

                <WSErrorMessage
                  contextKey="Other"
                  error={errors || updateRequirementDefinitionMeta.error}
                />

                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      kind: "Secondary",
                      label: "Back",
                      onClick: onBack,
                      type: "button"
                    },
                    {
                      kind: "Primary",
                      label: "Create version",
                      type: "submit"
                    }
                  ]}
                />
              </WSList>
            </WSForm>
          </WSList>
        );
      }}
    </WSQueries>
  );
};
